import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { partnerContactsApi } from '../../api/partner-contacts-offices-bank';
import { ApiError } from '../../api/client';
import { DataTable } from '../../components/DataTable';
import { IconActionButton } from '../../components/IconActionButton';
import { Check, UserX } from 'lucide-react';
import { useToast } from '../../components/Toast';

const CONTACT_ROLE_LABELS: Record<string, string> = {
  legal_representative: 'Legal Representative', highest_ranking_official: 'Highest Ranking Official',
  due_diligence_focal_person: 'Due Diligence Focal Person', programme_focal_person: 'Programme Focal Person',
  finance_focal_person: 'Finance Focal Person', meal_focal_person: 'M&E Focal Person',
  safeguarding_focal_person: 'Safeguarding/PSEA Focal Person', procurement_focal_person: 'Procurement Focal Person',
};

export function PartnerContactsTab({ partnerId }: { partnerId: number }) {
  const { showSuccess, showError } = useToast();
  const queryClient = useQueryClient();
  const contactsQuery = useQuery({ queryKey: ['partner-contacts', partnerId], queryFn: () => partnerContactsApi.list(partnerId) });
  const { register, handleSubmit, reset } = useForm<{ contactRole: string; fullName: string; title: string; email: string; phone: string; nationality: string }>();

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['partner-contacts', partnerId] });
  const createMutation = useMutation({
    mutationFn: (values: Record<string, string>) => partnerContactsApi.create(partnerId, values),
    onSuccess: () => { showSuccess('Contact added.'); invalidate(); reset(); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to add contact.'),
  });
  const verifyMutation = useMutation({ mutationFn: (id: number) => partnerContactsApi.verify(id), onSuccess: () => { showSuccess('Verified.'); invalidate(); }, onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed.') });
  const deactivateMutation = useMutation({ mutationFn: (id: number) => partnerContactsApi.deactivate(id), onSuccess: () => { showSuccess('Deactivated.'); invalidate(); }, onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed.') });

  return (
    <div className="panel">
      <div className="panel-body">
        {contactsQuery.data && (
          <DataTable
            rows={contactsQuery.data.data}
            rowKey={(c) => c.id}
            emptyMessage="No contacts on file yet."
            columns={[
              { header: 'Role', render: (c) => CONTACT_ROLE_LABELS[c.contact_role] ?? c.contact_role },
              { header: 'Name', render: (c) => c.full_name },
              { header: 'Title', render: (c) => c.title ?? '-' },
              { header: 'Email', render: (c) => c.email ?? '-' },
              { header: 'Phone', render: (c) => c.phone ?? '-' },
              { header: 'Status', render: (c) => c.verification_status === 'verified' ? 'Verified' : 'Unverified' },
              {
                header: '', render: (c) => (
                  <span className="row-actions">
                    {c.verification_status !== 'verified' && <IconActionButton icon={Check} label="Verify" onClick={() => verifyMutation.mutate(c.id)} />}
                    {Number(c.is_active) === 1 && <IconActionButton icon={UserX} label="Deactivate" danger onClick={() => deactivateMutation.mutate(c.id)} />}
                  </span>
                ),
              },
            ]}
          />
        )}

        <h3>Add Contact</h3>
        <form className="inline-form" onSubmit={handleSubmit((values) => createMutation.mutate(values))}>
          <label>
            Role
            <select {...register('contactRole', { required: true })}>
              <option value="">- choose -</option>
              {Object.entries(CONTACT_ROLE_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </label>
          <label>Full Name<input {...register('fullName', { required: true })} /></label>
          <label>Title<input {...register('title')} /></label>
          <label>Email<input type="email" {...register('email')} /></label>
          <label>Phone<input {...register('phone')} /></label>
          <label>Nationality<input {...register('nationality')} /></label>
          <button type="submit" disabled={createMutation.isPending}>{createMutation.isPending ? 'Adding…' : 'Add Contact'}</button>
        </form>
      </div>
    </div>
  );
}
